import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "./PopuliUserPage.css";

const API = {
  list: "https://populi-user-listining-7kpxqstxwq-ey.a.run.app",
  talks: "https://populi-user-talks-7kpxqstxwq-ey.a.run.app",
  update: "https://populi-changerole-7kpxqstxwq-ey.a.run.app",
  bulkUpdate: "https://populi-changerole-bulk-7kpxqstxwq-ey.a.run.app",
};

const PREVIEW_HEADERS = ["Phone_Number", "Sicil_No", "İsim", "Role", "Aktif"];

const STATUS_LABELS = {
  0: "Onay Bekliyor",
  1: "Kullanım Onayı Bekliyor",
  2: "Aktif",
  3: "Bloklu",
};

function Icon({ name }) {
  const paths = {
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    excel: <path d="M14 3h5v18H5V3h5m-2 5 5 8m0-8-5 8" />,
    upload: <path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v5h14v-5" />,
    manage: <path d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0" />,
    history: <path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5m4-2v5l3 2" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
  };
  return <svg className="users-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function Modal({ title, wide = false, children, onClose }) {
  return (
    <div className="users-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`users-modal${wide ? " users-modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="users-modal__header">
          <h2>{title}</h2>
          <button type="button" className="users-icon-button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function appendFormData(values) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => formData.append(key, value));
  return formData;
}

async function postForm(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) throw new Error(`İstek başarısız (${response.status})`);
  return response.json();
}

function statusLabel(step) {
  return STATUS_LABELS[String(step)] || "Bilinmeyen Statü";
}

export default function PopuliUserPage({ userEmail: emailProp, onUnauthorized }) {
  const [users, setUsers] = useState([]);
  const [department, setDepartment] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [managedUser, setManagedUser] = useState(null);
  const [talks, setTalks] = useState(null);
  const [talksUser, setTalksUser] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null);
  const fileInputRef = useRef(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadUsers = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.list, { useremail: userEmail });
      setUsers(Array.isArray(data?.result) ? data.result : []);
      setDepartment(data?.dept || "");
    } catch (requestError) {
      setError(requestError.message || "Kullanıcılar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadUsers();
  }, [loadUsers, onUnauthorized, userEmail]);

  const filteredUsers = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("tr");
    if (!normalized) return users;
    return users.filter((user) => [
      user.telegram_id,
      user.refvalue,
      user.userName,
      user.role,
      statusLabel(user.step),
    ].filter(Boolean).join(" ").toLocaleLowerCase("tr").includes(normalized));
  }, [query, users]);

  async function updateUser({ processType, finalReference, name = "", role = "" }) {
    if (!managedUser) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await postForm(API.update, {
        telegramID: managedUser.telegram_id,
        userEmail,
        process_type: processType,
        finalRefecereValue: finalReference,
        userName: name,
        role,
      });
      if (!data.success) throw new Error(data.message || "İşlem başarısız.");
      setManagedUser(null);
      setNotice("Kullanıcı bilgisi başarıyla güncellendi.");
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || "Kullanıcı güncellenemedi.");
      setLoading(false);
    }
  }

  function approveUser(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    updateUser({
      processType: "approve",
      finalReference: String(form.get("reference") || "").trim(),
      name: String(form.get("name") || "").trim(),
      role: String(form.get("role") || ""),
    });
  }

  async function loadTalks(user) {
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.talks, {
        telegramID: user.telegram_id,
        useremail: userEmail,
      });
      setTalksUser(user);
      setTalks(Array.isArray(data?.result) ? data.result : []);
    } catch (requestError) {
      setError(requestError.message || "Konuşma geçmişi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  function exportUsers() {
    const rows = users.map((user) => ({
      "Telefon No": user.telegram_id,
      "Sicil No": user.refvalue,
      "Kullanıcı Adı": user.userName,
      Rol: user.role ?? "Onay bekliyor",
      Statü: statusLabel(user.step),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Users");
    XLSX.writeFile(workbook, "Users.xlsx");
  }

  async function selectExcelFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      setExcelFile(file);
      setExcelPreview(rows);
    } catch (parseError) {
      setExcelFile(null);
      setExcelPreview(null);
      setError("Excel dosyası okunamadı.");
    }
  }

  function closeExcelPreview() {
    setExcelFile(null);
    setExcelPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function uploadExcel() {
    if (!excelFile) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await postForm(API.bulkUpdate, { userEmail, file: excelFile });
      if (!data.success) {
        const detail = data.message || (data.errors ? JSON.stringify(data.errors) : "İşlem başarısız.");
        throw new Error(detail);
      }
      closeExcelPreview();
      setNotice("Excel kullanıcı verileri başarıyla yüklendi.");
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || "Excel verileri yüklenemedi.");
      setLoading(false);
    }
  }

  const existingTelegramIds = useMemo(
    () => new Set(users.map((user) => String(user.telegram_id))),
    [users],
  );

  if (!userEmail) {
    return <div className="users-page users-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="users-page">
      <section className="users-shell">
        <div className="users-toolbar">
          <div className="users-toolbar__actions">
            <button className="users-button users-button--secondary" onClick={loadUsers} disabled={loading}>
              <Icon name="refresh" /> Yenile
            </button>
            {department === "HR" && (
              <label className="users-button users-button--success">
                <Icon name="upload" /> Kullanıcı Ekleme
                <input
                  ref={fileInputRef}
                  className="users-visually-hidden"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={selectExcelFile}
                />
              </label>
            )}
            <button className="users-button users-button--excel" onClick={exportUsers} disabled={!users.length}>
              <Icon name="excel" /> Excel’e Aktar
            </button>
          </div>
          <label className="users-search">
            <span>Ara</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Kullanıcı, sicil veya rol ara..." />
          </label>
        </div>

        {error && <div className="users-alert users-alert--error" role="alert">{error}</div>}
        {notice && <div className="users-alert users-alert--success" role="status">{notice}</div>}

        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr><th>Telegram ID</th><th>Referans Kodu</th><th>Kullanıcı Adı</th><th>Rol</th><th>Statü</th><th>İşlemler</th></tr>
            </thead>
            <tbody>
              {!filteredUsers.length && <tr><td className="users-table__empty" colSpan="6">Kullanıcı bulunmuyor.</td></tr>}
              {filteredUsers.map((user) => (
                <tr key={user.telegram_id}>
                  <td>{user.telegram_id || "—"}</td>
                  <td>{user.refvalue || "—"}</td>
                  <td>{user.userName || "—"}</td>
                  <td className={user.role == null ? "users-role--pending" : ""}>{user.role ?? "Onay bekliyor"}</td>
                  <td><span className={`users-status users-status--${user.step}`}>{statusLabel(user.step)}</span></td>
                  <td>
                    <div className="users-row-actions">
                      <button className="users-icon-button users-icon-button--history" onClick={() => loadTalks(user)} aria-label="Konuşma geçmişini aç" title="Konuşma geçmişi">
                        <Icon name="history" />
                      </button>
                      <button className="users-icon-button users-icon-button--manage" onClick={() => setManagedUser(user)} aria-label="Kullanıcıyı yönet" title="Kullanıcıyı yönet">
                        <Icon name="manage" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {managedUser && (
        <Modal title={String(managedUser.step) === "0" ? "Kullanıcı Onayı" : "Kullanıcı Engelleme"} onClose={() => setManagedUser(null)}>
          {String(managedUser.step) === "0" ? (
            <form className="users-form" onSubmit={approveUser}>
              <label>İsim<input name="name" defaultValue={managedUser.userName || ""} required autoFocus /></label>
              <label>Sicil No<input name="reference" defaultValue={managedUser.refvalue || ""} required /></label>
              <label>Rol<select name="role" defaultValue="MİM"><option value="MİM">MİM</option></select></label>
              <div className="users-modal__actions">
                <button type="button" className="users-button users-button--danger" onClick={() => updateUser({ processType: "block", finalReference: managedUser.refvalue || "" })}>Engelle</button>
                <button type="submit" className="users-button users-button--success">Onayla</button>
              </div>
            </form>
          ) : (
            <div>
              <p className="users-confirmation"><strong>{managedUser.userName || managedUser.telegram_id}</strong> adlı kullanıcıyı engellemek istediğinize emin misiniz?</p>
              <div className="users-modal__actions">
                <button type="button" className="users-button users-button--ghost" onClick={() => setManagedUser(null)}>İptal</button>
                <button type="button" className="users-button users-button--danger" onClick={() => updateUser({ processType: "block", finalReference: managedUser.refvalue || "" })}>Engelle</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {talks && (
        <Modal title={`${talksUser?.userName || talksUser?.telegram_id || "Kullanıcı"} · Konuşma Geçmişi`} wide onClose={() => { setTalks(null); setTalksUser(null); }}>
          <div className="users-table-wrap users-table-wrap--modal">
            <table className="users-table users-table--talks">
              <thead><tr><th>Kullanıcı Sorusu</th><th>Bot Cevabı</th><th>Zaman</th></tr></thead>
              <tbody>
                {!talks.length && <tr><td className="users-table__empty" colSpan="3">Konuşma kaydı bulunmuyor.</td></tr>}
                {talks.map((talk, index) => (
                  <tr key={`${talk.request_time || "talk"}-${index}`}>
                    <td>{talk.user || "—"}</td><td>{talk.ai_response || "—"}</td><td>{talk.request_time || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {excelPreview && (
        <Modal title="Excel Önizleme" wide onClose={closeExcelPreview}>
          {excelPreview.length ? (
            <div className="users-table-wrap users-table-wrap--modal">
              <table className="users-table users-table--preview">
                <thead><tr>{PREVIEW_HEADERS.map((header) => <th key={header}>{header}</th>)}</tr></thead>
                <tbody>
                  {excelPreview.map((row, index) => {
                    const duplicate = existingTelegramIds.has(String(row.Phone_Number));
                    return (
                      <tr className={duplicate ? "users-preview--duplicate" : ""} key={`${row.Phone_Number || "row"}-${index}`}>
                        {PREVIEW_HEADERS.map((header) => <td key={header}>{String(row[header] ?? "")}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p>Excel dosyasında veri bulunamadı.</p>}
          <p className="users-preview-note">Kırmızı satırlar sistemde mevcut Telegram ID’lerini gösterir.</p>
          <div className="users-modal__actions">
            <button type="button" className="users-button users-button--ghost" onClick={closeExcelPreview}>İptal</button>
            <button type="button" className="users-button users-button--secondary" onClick={uploadExcel} disabled={!excelPreview.length}>Yükle</button>
          </div>
        </Modal>
      )}

      {loading && <div className="users-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
